import type { Candidate, CashFlow, RevenueMap, TaxYearBasedProjectPlanRecord } from '~/types';

/**
 * TaxYearPlanBuilder - Builds tax year plans from cash flows during post-processing
 *
 * The tax year plan combines cash flows by tax year to create a simplified view
 * of the project's financial performance on an annual basis. This is generated
 * as a post-processing step for every candidate (viable or not) to support
 * analysis and debugging.
 *
 * Note: This builder uses the web app's simplified TaxYearBasedProjectPlanRecord
 * structure which focuses on core metrics rather than the prototype's detailed
 * breakdown of bond payments and DRF adjustments.
 */
export class TaxYearPlanBuilder {
  /**
   * Build tax year plan for a candidate from its cash flows
   */
  static buildTaxYearPlan(candidate: Candidate): void {
    const { cashFlows, revenueMap } = candidate;

    if (!cashFlows || cashFlows.length === 0) {
      candidate.taxYearBasedProjectPlan = [];
      return;
    }

    const taxYearPlansMap = new Map<number, TaxYearBasedProjectPlanRecord>();

    // Group cash flows by tax year
    const cashFlowsByTaxYear = TaxYearPlanBuilder.groupCashFlowsByTaxYear(cashFlows);

    // Create a plan for each tax year
    for (const [taxYear, yearCashFlows] of cashFlowsByTaxYear) {
      const plan = TaxYearPlanBuilder.createTaxYearPlan(taxYear, yearCashFlows, revenueMap);
      taxYearPlansMap.set(taxYear, plan);
    }

    // Sort by tax year and store in candidate
    const sortedPlans = Array.from(taxYearPlansMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([_, plan]) => plan);

    candidate.taxYearBasedProjectPlan = sortedPlans;
  }

  /**
   * Build tax year plan from components (for standalone usage)
   * @param cashFlows - Cash flows to process
   * @param revenueMap - Optional revenue map for taxable value and millage lookup
   */
  static buildFromCashFlows(
    cashFlows: CashFlow[],
    revenueMap?: RevenueMap
  ): TaxYearBasedProjectPlanRecord[] {
    if (!cashFlows || cashFlows.length === 0) {
      return [];
    }

    const taxYearPlansMap = new Map<number, TaxYearBasedProjectPlanRecord>();
    const cashFlowsByTaxYear = TaxYearPlanBuilder.groupCashFlowsByTaxYear(cashFlows);

    for (const [taxYear, yearCashFlows] of cashFlowsByTaxYear) {
      const plan = TaxYearPlanBuilder.createTaxYearPlan(taxYear, yearCashFlows, revenueMap);
      taxYearPlansMap.set(taxYear, plan);
    }

    return Array.from(taxYearPlansMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([_, plan]) => plan);
  }

  /**
   * Group cash flows by tax year
   */
  private static groupCashFlowsByTaxYear(cashFlows: CashFlow[]): Map<number, CashFlow[]> {
    const cashFlowsByTaxYear = new Map<number, CashFlow[]>();

    for (const cashFlow of cashFlows) {
      const taxYear = cashFlow.relatedTaxBillYear;
      if (!cashFlowsByTaxYear.has(taxYear)) {
        cashFlowsByTaxYear.set(taxYear, []);
      }
      cashFlowsByTaxYear.get(taxYear)!.push(cashFlow);
    }

    return cashFlowsByTaxYear;
  }

  /**
   * Create tax year plan for a specific year from its cash flows
   */
  private static createTaxYearPlan(
    taxYear: number,
    yearCashFlows: CashFlow[],
    revenueMap?: RevenueMap
  ): TaxYearBasedProjectPlanRecord {
    // Get taxable value and millage from revenue map if available
    const revenueEntry = revenueMap ? revenueMap[taxYear] : undefined;
    const taxableValue = revenueEntry?.taxableValue ?? 0;
    const millage = revenueEntry?.millage ?? yearCashFlows[0]?.millageRate ?? 0;

    const plan: TaxYearBasedProjectPlanRecord = {
      taxYear,
      taxableValue,
      millage,
      totalRevenue: 0,
      existingDebt: 0,
      netRevenue: 0,
      bondPayments: [],
      totalBondPayments: 0,
      totalPayment: 0,
      drfAdjustment: 0,
      drfEndingBalance: 0,
    };

    // Aggregate principal by bond series across all cash flows in this tax year
    // Using a map to accumulate by bond series index (derived from order in first cash flow)
    const principalByBondIndex = new Map<number, number>();

    // Sum up values from all cash flows in this tax year
    for (const cashFlow of yearCashFlows) {
      plan.totalRevenue += cashFlow.paymentRevenuePortion;
      plan.existingDebt += cashFlow.debtPaymentAmount;
      plan.netRevenue += cashFlow.revenueMinusExistingDebt;
      plan.totalBondPayments += cashFlow.totalBondPayment;
      plan.totalPayment += cashFlow.totalBondPayment + cashFlow.debtPaymentAmount;
      plan.drfAdjustment += cashFlow.drfAdjustment;
      // Use the last cash flow's DRF ending balance for the year
      plan.drfEndingBalance = cashFlow.drfEndingBalance;

      // Aggregate principal by bond index
      cashFlow.bondPayments.forEach((bondPayment, index) => {
        const currentPrincipal = principalByBondIndex.get(index) ?? 0;
        principalByBondIndex.set(index, currentPrincipal + bondPayment.principal);
      });
    }

    // Convert map to array (bondPayments now contains principal per bond series)
    const maxIndex = Math.max(...Array.from(principalByBondIndex.keys()), -1);
    for (let i = 0; i <= maxIndex; i++) {
      plan.bondPayments.push(principalByBondIndex.get(i) ?? 0);
    }

    return plan;
  }

  /**
   * Validate that a candidate has the required data for tax year plan generation
   */
  static validateCandidateForPlanGeneration(candidate: Candidate): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!candidate.cashFlows || candidate.cashFlows.length === 0) {
      errors.push('Candidate has no cash flows to process');
    }

    if (!candidate.revenueMap || Object.keys(candidate.revenueMap).length === 0) {
      errors.push('Candidate has no revenue map for millage rate information');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Check if candidate already has a tax year plan
   */
  static hasTaxYearPlan(candidate: Candidate): boolean {
    return !!(candidate.taxYearBasedProjectPlan && candidate.taxYearBasedProjectPlan.length > 0);
  }

  /**
   * Clear existing tax year plan from candidate
   */
  static clearTaxYearPlan(candidate: Candidate): void {
    candidate.taxYearBasedProjectPlan = [];
  }
}
